import { useState } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2 } from "lucide-react";
import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";
import { ToolPageLayout } from "@/components/ToolPageLayout";

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
});

export function MarkdownPreviewer() {
  const [input, setInput] = useState("# Hello Markdown\n\nWrite some **markdown** here to see it render live!\n\n- List item 1\n- List item 2\n\n```javascript\nconsole.log('Code block');\n```");
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<"split" | "edit" | "preview">("split");

  const html = DOMPurify.sanitize(md.render(input));

  const handleCopy = () => {
    copyToClipboard(html).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Markdown Previewer Online"
      description="Write and preview Markdown text in real-time with our free online Markdown previewer tool."
      h1="Markdown Previewer"
      category="Misc Tools"
      categoryPath="/misc-tools"
      howToUse={[
        "Type or paste your Markdown text in the editor.",
        "Use the split-view to see the rendered HTML preview in real-time.",
        "Copy the generated HTML code."
      ]}
      faq={[
        { question: "Can I preview HTML?", answer: "Yes, this tool renders Markdown and supports HTML elements." }
      ]}
    >
      <div className="space-y-4 flex flex-col h-full min-h-[500px]">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between shrink-0 gap-4">
          <div className="flex flex-wrap bg-slate-100 p-1 rounded-md">
            <button 
              onClick={() => setMode("edit")}
              className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${mode === "edit" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
            >
              Editor Only
            </button>
            <button 
              onClick={() => setMode("split")}
              className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${mode === "split" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
            >
              Split View
            </button>
            <button 
              onClick={() => setMode("preview")}
              className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${mode === "preview" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
            >
              Preview Only
            </button>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={handleCopy} variant="outline" size="sm" className="gap-2">
              <Copy className="h-4 w-4" />
              {copied ? "Copied HTML!" : "Copy HTML"}
            </Button>
            <Button onClick={() => setInput("")} variant="ghost" size="sm" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
              Clear
            </Button>
          </div>
        </div>

        <div className={`flex-1 grid gap-4 min-h-0 ${mode === "split" ? "md:grid-cols-2" : "grid-cols-1"}`}>
          {(mode === "edit" || mode === "split") && (
            <div className="flex flex-col min-h-0">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-tighter mb-2">Markdown Input</label>
              <Textarea
                className="flex-1 font-mono text-sm resize-none bg-slate-50 focus:bg-white"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type markdown here..."
              />
            </div>
          )}
          
          {(mode === "preview" || mode === "split") && (
            <div className="flex flex-col min-h-0">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-tighter mb-2">Live Preview</label>
              <div 
                className="flex-1 border border-slate-200 rounded-lg p-6 overflow-y-auto bg-white prose prose-slate max-w-none"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  );
}
